import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Filter, ChevronDown, ChevronUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import GlobalFilters from "@/components/GlobalFilters";
import { DEFAULT_FILTERS } from "@/hooks/useFilters";

/**
 * Phase 5.9 — Collapsible wrapper around <GlobalFilters> (and, optionally,
 * arbitrary children for pages with custom filter UI like Catalog).
 *
 * Always starts collapsed on each navigation. Header shows the count of
 * non-default filters so the user knows there's "stuff applied" even when
 * the body is hidden. Counter resets to 0 (badge hidden) when Reset is
 * pressed because we compare against `DEFAULT_FILTERS` from the same hook
 * that backs the page.
 *
 * Props:
 *   - filters / onChange / onReset: passed straight through to GlobalFilters.
 *   - activeCount?: number — override the auto-computed counter (use this
 *     when wrapping custom filters that aren't `useFilters`-shaped).
 *   - defaultExpanded?: bool — escape hatch; user requirement is always
 *     collapsed.
 *   - children?: ReactNode — when provided, renders instead of GlobalFilters
 *     (lets Catalog reuse the same chrome with its inline pill filters).
 *   - title?: string — header label; defaults to t('filters.title').
 */

export function countActiveFilters(f, defaults = DEFAULT_FILTERS) {
  if (!f) return 0;
  let n = 0;
  if (f.preset !== undefined && f.preset !== defaults.preset) n += 1;
  if (f.preset === "custom") {
    if (f.date_from && f.date_from !== defaults.date_from) n += 1;
    if (f.date_to && f.date_to !== defaults.date_to) n += 1;
  }
  if (
    f.source_report !== undefined &&
    f.source_report !== defaults.source_report
  ) {
    n += 1;
  }
  if (Array.isArray(f.transaction_types) && f.transaction_types.length > 0) n += 1;
  if (Array.isArray(f.department_ids) && f.department_ids.length > 0) n += 1;
  if (Array.isArray(f.employee_ids) && f.employee_ids.length > 0) n += 1;
  return n;
}

export default function CollapsibleFiltersPanel({
  filters,
  onChange,
  onReset,
  defaultExpanded = false,
  activeCount,
  children,
  title,
  testId = "collapsible-filters-panel",
  storageKey,
}) {
  const { t } = useTranslation();

  // Persistencia opcional en localStorage por página (key derivado del testId
  // si no se pasa explícito). Default = cerrado la primera vez.
  const lsKey = useMemo(() => {
    if (storageKey) return storageKey;
    return `filters_panel_open:${testId}`;
  }, [storageKey, testId]);

  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return !!defaultExpanded;
    try {
      const stored = window.localStorage.getItem(lsKey);
      if (stored === "1") return true;
      if (stored === "0") return false;
    } catch {
      // localStorage puede fallar en modo privado; usamos default.
    }
    return !!defaultExpanded;
  });

  const toggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(lsKey, next ? "1" : "0");
      } catch {
        // Ignorar fallos de almacenamiento.
      }
      return next;
    });
  };

  const computedCount = useMemo(
    () => (activeCount !== undefined ? activeCount : countActiveFilters(filters)),
    [filters, activeCount]
  );

  const baseLabel = title || t("filters.title", { defaultValue: "Filtros" });
  const headerLabel = open
    ? t("filters.hideFilters", { defaultValue: "Ocultar filtros" })
    : t("filters.showFilters", { defaultValue: "Mostrar filtros" });

  return (
    <section
      className="rounded-lg border border-border bg-card/50 overflow-hidden"
      data-testid={testId}
      data-expanded={open}
      data-active-count={computedCount}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="filters-panel-body"
        aria-label={`${headerLabel}${computedCount > 0 ? ` (${computedCount})` : ""}`}
        onClick={toggleOpen}
        data-testid="filters-panel-header"
        className={cn(
          "flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium transition-colors",
          "hover:bg-muted/40 focus:outline-none focus:ring-1 focus:ring-primary/40"
        )}
      >
        <span className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-primary" />
          <span className="sr-only">{baseLabel}: </span>
          <span data-testid="filters-panel-toggle-label">{headerLabel}</span>
          {computedCount > 0 && (
            <Badge
              variant="secondary"
              className="h-4 px-1.5 text-[10px] font-semibold"
              data-testid="filters-panel-badge"
            >
              {computedCount}
            </Badge>
          )}
        </span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      <div
        id="filters-panel-body"
        data-testid="filters-panel-content"
        hidden={!open}
        className={cn(
          "border-t border-border bg-background/40",
          open ? "animate-in fade-in slide-in-from-top-1 duration-200" : ""
        )}
      >
        {open && (children !== undefined ? (
          children
        ) : (
          <GlobalFilters filters={filters} onChange={onChange} onReset={onReset} />
        ))}
      </div>
    </section>
  );
}
